/**
 * SignInPrompt - Consistent sign-in prompt component used across the app.
 * Uses the same CTA style as the home page Final CTA Section.
 */

import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, Mic, ArrowRight } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Button } from './button';

const signInPromptVariants = cva(
  'text-center',
  {
    variants: {
      variant: {
        overlay: 'p-8',
        standalone: 'py-16',
      },
    },
    defaultVariants: {
      variant: 'standalone',
    },
  }
);

export interface SignInPromptProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof signInPromptVariants> {
  /** Title text - defaults to "Sign in to get started" */
  title?: string;
  /** Description text below the title */
  description?: string;
  /** Icon to display - defaults to Lock */
  icon?: React.ReactNode;
  /** Primary button color variant - kept for backwards compatibility */
  buttonVariant?: 'emerald' | 'indigo' | 'blue';
}

const SignInPrompt = React.forwardRef<HTMLDivElement, SignInPromptProps>(
  (
    {
      className,
      variant,
      title,
      description,
      icon,
      ...props
    },
    ref
  ) => {
    const location = useLocation();
    const { t } = useTranslation();

    const resolvedTitle = title ?? t('signIn.title');
    const resolvedDescription = description ?? t('signIn.description');

    return (
      <div
        ref={ref}
        className={cn(signInPromptVariants({ variant }), className)}
        {...props}
      >
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
          {icon || <Lock className="w-8 h-8 text-slate-400" />}
        </div>
        <h3 className="text-xl font-semibold text-slate-900 mb-2">{resolvedTitle}</h3>
        <p className="text-slate-600 mb-6 max-w-sm mx-auto">{resolvedDescription}</p>

        {/* CTA Buttons - matching home page Final CTA style */}
        <div className="flex flex-col items-center gap-3">
          <Button asChild variant="gradient" size="lg" shape="rounded" className="group">
            <Link to="/signup" state={{ from: location.pathname }}>
              <Mic className="w-5 h-5" />
              {t('signIn.startFreeTrial')}
              <ArrowRight
                size={18}
                className="group-hover:translate-x-1 transition-transform"
              />
            </Link>
          </Button>
          <Link
            to="/login"
            state={{ from: location.pathname }}
            className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
          >
            {t('signIn.alreadyHaveAccount')}
          </Link>
          <p className="text-sm text-slate-400">{t('signIn.freeTrialNote')}</p>
        </div>
      </div>
    );
  }
);
SignInPrompt.displayName = 'SignInPrompt';

export { SignInPrompt, signInPromptVariants };
